import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import {
  Box,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { toast } from 'react-toastify';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { TicketFileInput } from '@bumble/app/__generated__/Api';

const handleUploadError = (error: unknown, filename: string) => {
  if (axios.isAxiosError(error)) {
    toast.error(`Failed to upload ${filename}.`, { autoClose: false, hideProgressBar: true });
    return;
  }
  apiError(error);
};

interface PendingUpload {
  key: string;
  filename: string;
  progress: number;
  error?: boolean;
}

interface TicketAttachmentUploaderProps {
  attachments: TicketFileInput[];
  onAttachmentsChange: Dispatch<SetStateAction<TicketFileInput[]>>;
  onUploadingChange?: (isUploading: boolean) => void;
  disabled?: boolean;
}

export const TicketAttachmentUploader = ({
  attachments,
  onAttachmentsChange,
  onUploadingChange,
  disabled,
}: TicketAttachmentUploaderProps) => {
  const api = useApi();
  const [pending, setPending] = useState<PendingUpload[]>([]);

  useEffect(() => {
    onUploadingChange?.(pending.some((p) => !p.error));
  }, [pending, onUploadingChange]);

  const uploadFile = useCallback(
    async (file: File) => {
      const key = `${file.name}-${file.size}-${file.lastModified}`;
      setPending((prev) => [...prev, { key, filename: file.name, progress: 0 }]);

      try {
        const { data: uploadUrl } = await api.getTicketUploadUrlApiV1GetTicketUploadUrlGet({
          filename: file.name,
        });

        await axios.put(uploadUrl.presigned_url, file, {
          headers: { 'Content-Type': '' },
          onUploadProgress: (progressEvent) => {
            const { loaded, total } = progressEvent;
            const percentage = total ? Math.floor((loaded * 100) / total) : 0;
            setPending((prev) => prev.map((p) => (p.key === key ? { ...p, progress: percentage } : p)));
          },
        });

        onAttachmentsChange((prev) => [
          ...prev,
          {
            filename: uploadUrl.filename,
            bucket: uploadUrl.s3_bucket,
            object_key: uploadUrl.object_key,
            presigned_url: uploadUrl.presigned_url,
          },
        ]);
        setPending((prev) => prev.filter((p) => p.key !== key));
      } catch (error) {
        handleUploadError(error, file.name);
        setPending((prev) => prev.map((p) => (p.key === key ? { ...p, error: true } : p)));
      }
    },
    [api, onAttachmentsChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    disabled,
    multiple: true,
    onDrop: (acceptedFiles) => {
      acceptedFiles.forEach(uploadFile);
    },
  });

  const removeAttachment = async (file: TicketFileInput) => {
    try {
      await api.deleteTicketUploadApiV1DeleteTicketUploadPost({ object_key: file.object_key });
      onAttachmentsChange((prev) => prev.filter((f) => f.object_key !== file.object_key));
    } catch (error) {
      apiError(error);
    }
  };

  return (
    <Box>
      <Box
        {...getRootProps()}
        sx={{
          border: '1px dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          borderRadius: 1,
          p: 2,
          textAlign: 'center',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <input {...getInputProps()} />
        <Typography variant="body2" color="text.secondary">
          Drag and drop files here, or click to select files.
        </Typography>
      </Box>

      {(attachments.length > 0 || pending.length > 0) && (
        <List dense disablePadding sx={{ mt: 1 }}>
          {attachments.map((file) => (
            <ListItem
              key={file.object_key}
              disableGutters
              secondaryAction={
                <IconButton
                  edge="end"
                  size="small"
                  disabled={disabled}
                  onClick={() => removeAttachment(file)}
                  aria-label={`Remove ${file.filename}`}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              <AttachFileIcon fontSize="small" sx={{ mr: 1 }} />
              <ListItemText primary={file.filename} />
            </ListItem>
          ))}
          {pending.map((p) => (
            <ListItem key={p.key} disableGutters>
              <Box sx={{ width: '100%' }}>
                <Typography variant="body2" color={p.error ? 'error' : 'text.primary'}>
                  {p.filename}
                  {p.error ? ' — upload failed' : ''}
                </Typography>
                {!p.error && <LinearProgress variant="determinate" value={p.progress} sx={{ mt: 0.5 }} />}
              </Box>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};
